import { useState } from "react"

import { ConfirmDeleteModal } from "../shared/ConfirmDeleteModal"
import { GuestHospitalityForm } from "./GuestHospitalityForm"
import { GuestHospitalityList } from "./GuestHospitalityList"
import { hospitalityCategoryOptions } from "./guestHospitalityOptions"
import { PlaybookGuidance } from "./PlaybookGuidance"
import type {
  StayGuestHospitalityCategory,
  StayGuestHospitalityData,
  StayGuestHospitalityEntry,
  StayGuestHospitalityTarget,
  StayPlaybookTemplates,
} from "./stayData"
import { VacationOverviewCard } from "./VacationOverviewCard"

type GuestHospitalitySectionProps = {
  authenticityToken: string
  hospitality: StayGuestHospitalityData
  playbookTemplates: StayPlaybookTemplates
  selectedTravellerId?: string
  vacationId: string
}

export function GuestHospitalitySection({
  authenticityToken,
  hospitality,
  playbookTemplates,
  selectedTravellerId,
  vacationId,
}: GuestHospitalitySectionProps) {
  const [notes, setNotes] = useState(hospitality.notes)
  const [activeCategory, setActiveCategory] = useState<StayGuestHospitalityCategory | null>(null)
  const [selectedTargetId, setSelectedTargetId] = useState<string | null>(null)
  const [description, setDescription] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [notePendingRemoval, setNotePendingRemoval] = useState<StayGuestHospitalityEntry | null>(null)
  const profileTraveller = selectedTravellerId
    ? hospitality.travellers.find((traveller) => traveller.id === selectedTravellerId)
    : null
  const visibleTravellers = profileTraveller ? [profileTraveller] : hospitality.travellers
  const visibleNotes = profileTraveller
    ? notes.filter((note) => !note.guest_id || note.guest_id === profileTraveller.id)
    : notes
  const profileEntries = visibleTravellers.flatMap((traveller) => {
    const entries: StayGuestHospitalityEntry[] = []

    if (traveller.drinks_preference) {
      entries.push({
        id: `profile-${traveller.id}-drinks-preference`,
        category: "food_and_drink",
        text: traveller.drinks_preference,
        source: "profile",
        guest_id: traveller.id,
      })
    }

    if (traveller.special_interests) {
      entries.push({
        id: `profile-${traveller.id}-special-interests`,
        category: "guiding",
        text: traveller.special_interests,
        source: "profile",
        guest_id: traveller.id,
      })
    }

    return entries
  })
  const entries = [...visibleNotes, ...profileEntries]
  const listTargets: readonly StayGuestHospitalityTarget[] = profileTraveller ? [
    { id: "", label: "Whole party" },
    {
      id: profileTraveller.id,
      label: profileTraveller.display_name,
    },
  ] : [
    { id: "", label: "Whole party" },
    ...visibleTravellers.map((traveller) => ({
      id: traveller.id,
      label: traveller.display_name,
    })),
  ]
  const formTargets: readonly StayGuestHospitalityTarget[] = profileTraveller
    ? [{ id: profileTraveller.id, label: profileTraveller.display_name }]
    : listTargets
  const activeCategoryOption = hospitalityCategoryOptions.find((category) => category.value === activeCategory)
  const canAddNote = Boolean(activeCategory && selectedTargetId !== null && description.trim() && !isSaving)

  function openCategory(category: StayGuestHospitalityCategory) {
    setActiveCategory(category)
    setSelectedTargetId(profileTraveller?.id ?? null)
    setDescription("")
    setError(null)
  }

  function closeForm() {
    if (isSaving) return

    setActiveCategory(null)
    setSelectedTargetId(null)
    setDescription("")
    setError(null)
  }

  async function addNote() {
    if (!canAddNote || !activeCategory) return

    setError(null)
    setIsSaving(true)

    try {
      const note = await createHospitalityNote(
        vacationId,
        {
          category: activeCategory,
          description: description.trim(),
          traveller_id: selectedTargetId || null,
        },
        authenticityToken,
      )

      setNotes((currentNotes) => [...currentNotes, note])
      setActiveCategory(null)
      setSelectedTargetId(null)
      setDescription("")
    } catch {
      setError("Hospitality note could not be added.")
    } finally {
      setIsSaving(false)
    }
  }

  function openRemoveConfirmation(entry: StayGuestHospitalityEntry) {
    if (entry.source !== "note") return

    setNotePendingRemoval(entry)
  }

  function closeRemoveConfirmation() {
    setNotePendingRemoval(null)
  }

  async function removeNote(entry: StayGuestHospitalityEntry) {
    if (entry.source !== "note") return

    setError(null)
    setIsSaving(true)

    try {
      await deleteHospitalityNote(vacationId, entry.id, authenticityToken)
      setNotes((currentNotes) => currentNotes.filter((currentNote) => currentNote.id !== entry.id))
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <section className="flex min-w-0 flex-col gap-5">
      <h2 className="text-base font-semibold uppercase leading-5 tracking-[0.96px] text-[#525252]">
        Guest Hospitality
      </h2>

      <div className="flex flex-col gap-6">
        {hospitalityCategoryOptions.map((category) => {
          const isOpen = activeCategory === category.value
          const playbookCopy = playbookTemplates[category.playbookKey]
          const hasEntries = entries.some((entry) => entry.category === category.value)

          return (
            <VacationOverviewCard
              actionExpanded={isOpen}
              actionHasPopup="dialog"
              actionLabel={`Add ${category.label} note`}
              key={category.value}
              onAction={() => openCategory(category.value)}
              title={category.label}
            >
              <PlaybookGuidance copy={playbookCopy} topic={category.label} />

              <GuestHospitalityList
                category={category.value}
                entries={entries}
                isSaving={isSaving}
                onRemoveNote={openRemoveConfirmation}
                targets={listTargets}
              />

              {!hasEntries && !playbookCopy && !isOpen ? (
                <p className="rounded-[4px] border border-dashed border-[#cfcfcf] bg-[#fafafa] px-3 py-2 text-sm font-medium leading-5 text-[#817d76]">
                  No {category.label.toLowerCase()} notes yet.
                </p>
              ) : null}
            </VacationOverviewCard>
          )
        })}
      </div>

      {activeCategoryOption ? (
        <GuestHospitalityForm
          canAddNote={canAddNote}
          category={activeCategoryOption}
          description={description}
          error={error}
          isSaving={isSaving}
          onAddNote={() => void addNote()}
          onClose={closeForm}
          onDescriptionChange={setDescription}
          onTargetChange={setSelectedTargetId}
          selectedTargetId={selectedTargetId}
          targets={formTargets}
        />
      ) : null}

      {notePendingRemoval ? (
        <ConfirmDeleteModal
          errorMessage="Hospitality note could not be removed."
          message="Are you sure you want to delete this hospitality note?"
          onClose={closeRemoveConfirmation}
          onConfirm={() => removeNote(notePendingRemoval)}
          title="Delete hospitality note"
        />
      ) : null}
    </section>
  )
}

async function createHospitalityNote(
  vacationId: string,
  attributes: {
    category: StayGuestHospitalityCategory
    description: string
    traveller_id: string | null
  },
  authenticityToken: string,
) {
  const response = await fetch(`/api/vacations/${vacationId}/hospitality_notes`, {
    body: JSON.stringify({ hospitality_note: attributes }),
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": authenticityToken,
    },
    method: "POST",
  })

  if (!response.ok) {
    throw new Error(`Hospitality note create failed with ${response.status}`)
  }

  return (await response.json()) as StayGuestHospitalityEntry
}

async function deleteHospitalityNote(vacationId: string, noteId: string, authenticityToken: string) {
  const response = await fetch(`/api/vacations/${vacationId}/hospitality_notes/${noteId}`, {
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": authenticityToken,
    },
    method: "DELETE",
  })

  if (!response.ok) {
    throw new Error(`Hospitality note delete failed with ${response.status}`)
  }
}
